import { PhotoPickerDialog } from '@apps/admin/routes/WebsiteOverview/routes/Photos/components';
import React, { useMemo } from 'react';
import { useWebsitePhotoUpload } from './useWebsitePhotoUpload';
import { useEventInfo } from '@shared/utils/eventInfo';
import { AddMediaArgs, PICKER_TYPES } from '@apps/admin/routes/WebsiteOverview/routes/Photos/components/PhotoPickerDialog/PhotoPickerDialog.types';
import { cloneDeep } from 'lodash-es';
import { MEDIA_SERVICE } from '@apps/card/routes/CardCustomizer/steps/CardDesign/config';
import { useUploadedPreviewImage } from '@apps/admin/routes/WebsiteDesignerV2/routes/Template/hooks/useUploadedPreviewImage';

type EventPhotoPickerProps = {
  onPhotoSelect: (photo: { id: string; url: string }) => void;
  selectedPhotoUrl?: string | null;
  isOpen?: boolean;
  onClose?: () => void;
};

export const EventPhotoPicker = (props: EventPhotoPickerProps) => {
  const { eventInfo } = useEventInfo();
  const { onPhotoSelect, isOpen, onClose, selectedPhotoUrl } = props;
  const { media: mediaRaw, isLoadingEventMedia } = useUploadedPreviewImage();

  // Add rendition query param to reduce image size for picker
  const media = useMemo(() => {
    return mediaRaw.map(photo => {
      const photoWithRendition = cloneDeep(photo);
      const url = photoWithRendition.photo.url;

      if (url.includes(MEDIA_SERVICE) && !url.includes('rendition')) {
        photoWithRendition.photo.url = `${url}?rendition=small`;
      }

      return photoWithRendition;
    });
  }, [mediaRaw]);

  const selectedItems = useMemo(() => {
    return selectedPhotoUrl ? media.filter(m => m.photo.url === selectedPhotoUrl).map(m => m.id) : [];
  }, [media, selectedPhotoUrl]);

  const { openFilePicker, fileUploadStatusData } = useWebsitePhotoUpload({
    eventId: eventInfo?.eventId ?? '',
    // Container ID is event ID for the event's photo picker gallery.
    containerId: eventInfo?.eventId ?? '',
  });

  const handleSelect = ({ selectedItemsId }: AddMediaArgs) => {
    // Pick image from mediaRaw
    const selectedImage = mediaRaw.find(m => m.id === selectedItemsId[0]);
    if (selectedImage) {
      onPhotoSelect({ id: selectedImage.id, url: selectedImage.photo.url });
      onClose?.();
    }
  };

  return (
    <PhotoPickerDialog
      minCount={1}
      maxCount={1}
      selectedItems={selectedItems}
      disabledItems={[]}
      collectionId={undefined}
      isOpen={isOpen ?? false}
      handleClose={() => onClose?.()}
      handleOpenFilePicker={openFilePicker}
      isFetching={isLoadingEventMedia}
      media={media}
      handleAddMedia={handleSelect}
      pickerType={PICKER_TYPES.PICK_FROM_ALL_PHOTOS}
      isAddingMedia={false}
      uploadInProgressCount={fileUploadStatusData.isUploading ? fileUploadStatusData.imageCount : 0}
    />
  );
};
